// P5 (2026-11-02) — Bulk week scheduler.
//
// Add rows (date+time, approved set, surfaces as cells), see a preview
// table with warnings for past times and duplicate set+surface on the
// same day, then confirm once to create every schedule in one atomic
// batch. If any row fails validation, nothing is written.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import {
  channelDisplay,
  fmtDateTimeTz,
  parseDatetimeLocalInTz,
  useOrgTimezone,
} from "../lib/tz";

const CHANNELS = ["facebook", "instagram", "pinterest", "tiktok", "youtube"];
const SURFACE_OPTS = ["feed", "story", "reel", "carousel", "pin", "video_pin", "photo"];

function newSlot(def = {}) {
  return {
    scheduled_for: def.scheduled_for || "",
    asset_set_id:  def.asset_set_id || "",
    note:          def.note || "",
    cells:         def.cells || [{ channel: "facebook", surface: "feed",
                                    channel_config: {} }],
  };
}

export default function PublishScheduleWeek() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const [sets, setSets] = useState([]);
  const [slots, setSlots] = useState([newSlot()]);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isAcceptanceTest, setIsAcceptanceTest] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/asset-sets/publishable?limit=100");
      setSets(r.data?.asset_sets || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function patchSlot(i, patch) {
    setSlots((all) => all.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function patchCell(slotIdx, cellIdx, patch) {
    setSlots((all) => all.map((s, i) => {
      if (i !== slotIdx) return s;
      const cells = s.cells.map((c, ci) => ci === cellIdx ? { ...c, ...patch } : c);
      return { ...s, cells };
    }));
  }
  function addSlot() { setSlots((all) => [...all, newSlot()]); }
  function removeSlot(i) { setSlots((all) => all.filter((_, idx) => idx !== i)); }
  function addCell(i) {
    setSlots((all) => all.map((s, idx) => idx === i
      ? { ...s, cells: [...s.cells, { channel: "facebook", surface: "feed", channel_config: {} }] }
      : s));
  }
  function removeCell(i, ci) {
    setSlots((all) => all.map((s, idx) => idx === i
      ? { ...s, cells: s.cells.filter((_, j) => j !== ci) }
      : s));
  }

  const payload = useMemo(() => ({
    slots: slots.map((s) => ({
      // P5.1 (2026-11-03) — Interpret the typed datetime-local in the
      // org's timezone so a "7 AM" slot is 7 AM ET on both sides of
      // the Nov DST switch, not 7 AM device-local.
      scheduled_for: s.scheduled_for
        ? (parseDatetimeLocalInTz(s.scheduled_for, tz) || new Date(s.scheduled_for)).toISOString()
        : null,
      asset_set_id:  s.asset_set_id,
      note:          s.note || null,
      cells:         s.cells,
    })),
    is_acceptance_test: isAcceptanceTest,
  }), [slots, isAcceptanceTest, tz]);

  async function onPreview() {
    setErr(null); setPreview(null); setPreviewing(true);
    try {
      const r = await api.post("/publish/schedules/bulk-preview", payload);
      setPreview(r.data || null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  }

  async function onCreate() {
    if (!preview?.ok) return;
    setErr(null); setCreating(true);
    try {
      const r = await api.post("/publish/schedules/bulk", payload);
      const batch = r.data?.bulk_batch_id || "";
      alert(`Created ${r.data?.created_schedules || 0} schedules`
            + (batch ? `\nbatch id: ${batch}` : ""));
      navigate("/brand-os/publish/queue");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Shell title="Week scheduler">
      <div className="bo-page" data-testid="schedule-week-page"
           style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div className="bo-eyebrow">Publish Infrastructure</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: 0 }}>
              Week scheduler
            </h1>
            <p style={{ color: "var(--bo-muted)", maxWidth: 640, marginTop: 4 }}>
              Add rows for the week, preview, then create every schedule in a
              single atomic batch. If any row fails validation, nothing is
              written. Times entered in <strong>{tz.timezone}{tz.abbr ? ` (${tz.abbr})` : ""}</strong>.
            </p>
          </div>
          <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>
            ← Queue
          </Link>
        </div>

        {err && (
          <div className="bo-card" data-testid="schedule-week-error"
               style={{ padding: 12, color: "var(--bo-danger)", marginBottom: 12 }}>
            {typeof err === "string" ? err : err.message || JSON.stringify(err)}
          </div>
        )}

        <div className="bo-card" style={{ padding: 12, marginBottom: 12,
                                           display: "flex", gap: 12,
                                           alignItems: "center" }}>
          <label style={{ fontSize: 13, display: "flex", gap: 8,
                          alignItems: "center" }}>
            <input
              type="checkbox"
              data-testid="schedule-week-acceptance"
              checked={isAcceptanceTest}
              onChange={(e) => setIsAcceptanceTest(e.target.checked)}
            />
            Mark whole batch as acceptance tests
          </label>
        </div>

        {slots.map((s, i) => (
          <div key={i} className="bo-card"
               data-testid={`schedule-week-slot-${i}`}
               style={{ padding: 14, marginBottom: 10,
                        display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
                          alignItems: "flex-end" }}>
              <div style={{ display: "grid", gap: 4 }}>
                <label className="bo-label">
                  When ({tz.timezone}{tz.abbr ? ` · ${tz.abbr}` : ""})
                </label>
                <input
                  type="datetime-local"
                  className="bo-input"
                  data-testid={`schedule-week-slot-${i}-when`}
                  value={s.scheduled_for}
                  onChange={(e) => patchSlot(i, { scheduled_for: e.target.value })}
                />
              </div>
              <div style={{ display: "grid", gap: 4, flex: 1, minWidth: 240 }}>
                <label className="bo-label">Approved set</label>
                <select
                  className="bo-input"
                  data-testid={`schedule-week-slot-${i}-set`}
                  value={s.asset_set_id}
                  onChange={(e) => patchSlot(i, { asset_set_id: e.target.value })}
                >
                  <option value="">— pick an approved set —</option>
                  {sets.map((x) => (
                    <option key={x.id} value={x.id}>
                      {(x.name || x.title || x.id)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 4, minWidth: 240 }}>
                <label className="bo-label">Note (optional)</label>
                <input
                  className="bo-input"
                  data-testid={`schedule-week-slot-${i}-note`}
                  value={s.note}
                  onChange={(e) => patchSlot(i, { note: e.target.value })}
                />
              </div>
              <button className="bo-btn bo-btn-ghost"
                      data-testid={`schedule-week-slot-${i}-remove`}
                      onClick={() => removeSlot(i)}
                      style={{ fontSize: 12 }}>
                Remove row
              </button>
            </div>
            <div style={{ borderTop: "1px dashed var(--bo-line)", paddingTop: 10 }}>
              <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Cells</div>
              {s.cells.map((c, ci) => (
                <div key={ci}
                     data-testid={`schedule-week-slot-${i}-cell-${ci}`}
                     style={{ display: "flex", gap: 8, marginBottom: 6,
                              alignItems: "center" }}>
                  <select
                    className="bo-input"
                    data-testid={`schedule-week-slot-${i}-cell-${ci}-channel`}
                    value={c.channel}
                    onChange={(e) => patchCell(i, ci, { channel: e.target.value })}
                    style={{ width: 140 }}
                  >
                    {CHANNELS.map((ch) => <option key={ch} value={ch}>{channelDisplay(ch)}</option>)}
                  </select>
                  <select
                    className="bo-input"
                    data-testid={`schedule-week-slot-${i}-cell-${ci}-surface`}
                    value={c.surface}
                    onChange={(e) => patchCell(i, ci, { surface: e.target.value })}
                    style={{ width: 140 }}
                  >
                    {SURFACE_OPTS.map((sfc) => <option key={sfc} value={sfc}>{sfc}</option>)}
                  </select>
                  <input
                    className="bo-input"
                    data-testid={`schedule-week-slot-${i}-cell-${ci}-caption`}
                    placeholder="caption"
                    value={c.channel_config?.caption || ""}
                    onChange={(e) => patchCell(i, ci, {
                      channel_config: { ...(c.channel_config || {}),
                                         caption: e.target.value },
                    })}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="bo-btn bo-btn-ghost"
                    data-testid={`schedule-week-slot-${i}-cell-${ci}-remove`}
                    onClick={() => removeCell(i, ci)}
                    style={{ fontSize: 12 }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="bo-btn bo-btn-ghost"
                      data-testid={`schedule-week-slot-${i}-add-cell`}
                      onClick={() => addCell(i)}
                      style={{ fontSize: 12 }}>
                + Add cell
              </button>
            </div>
          </div>
        ))}
        <button className="bo-btn bo-btn-ghost"
                data-testid="schedule-week-add-slot"
                onClick={addSlot}
                style={{ marginBottom: 16 }}>
          + Add row
        </button>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="bo-btn"
            data-testid="schedule-week-preview-btn"
            onClick={onPreview}
            disabled={previewing}
          >
            {previewing ? "Previewing…" : "Preview"}
          </button>
          <button
            className="bo-btn"
            data-testid="schedule-week-create-btn"
            onClick={onCreate}
            disabled={creating || !preview || !preview.ok}
            style={{ opacity: (!preview || !preview.ok) ? 0.5 : 1 }}
          >
            {creating ? "Creating…" : "Create batch"}
          </button>
        </div>

        {preview && (
          <div className="bo-card"
               data-testid="schedule-week-preview-card"
               style={{ padding: 14, marginTop: 16 }}>
            <div className="bo-eyebrow" style={{ marginBottom: 8 }}>
              Preview {preview.ok ? "— OK" : "— rejected"}
            </div>
            {preview.errors?.length > 0 && (
              <div data-testid="schedule-week-preview-errors"
                   style={{ marginBottom: 10, color: "var(--bo-danger)" }}>
                <strong>Errors (blocking):</strong>
                <ul>
                  {preview.errors.map((e, idx) => (
                    <li key={idx}>Row {e.slot_index}: {e.code} — {e.message}</li>
                  ))}
                </ul>
              </div>
            )}
            {preview.warnings?.length > 0 && (
              <div data-testid="schedule-week-preview-warnings"
                   style={{ marginBottom: 10,
                            color: "var(--bo-warn, #a05a00)" }}>
                <strong>Warnings:</strong>
                <ul>
                  {preview.warnings.map((w, idx) => (
                    <li key={idx}>Row {w.slot_index}: {w.code} — {w.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 6 }}>Row</th>
                  <th style={{ textAlign: "left", padding: 6 }}>When</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Set</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Cells</th>
                </tr>
              </thead>
              <tbody>
                {(preview.normalized_slots || []).map((row) => (
                  <tr key={row.index}
                      data-testid={`schedule-week-preview-row-${row.index}`}
                      style={{ borderTop: "1px solid var(--bo-line)" }}>
                    <td style={{ padding: 6 }}>{row.index + 1}</td>
                    <td style={{ padding: 6, fontFamily: "var(--bo-mono, monospace)" }}>
                      {row.scheduled_for ? fmtDateTimeTz(row.scheduled_for, tz) : "—"}
                    </td>
                    <td style={{ padding: 6, fontFamily: "var(--bo-mono, monospace)" }}>
                      {(row.asset_set_id || "").slice(0, 10)}
                    </td>
                    <td style={{ padding: 6 }}>
                      {(row.cells || []).map((c, idx) => (
                        <span key={idx} style={{ marginRight: 8 }}>
                          {channelDisplay(c.channel)}:{c.surface}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}
